/**
 * As avaliações das sessões (docs/spec/session-clock.md, "Avaliações") como a aba Sessões, o relógio
 * e o perfil as mostram: o prazo, o que o cartão da sessão diz, a média e as linhas do perfil. Puro:
 * quem desenha passa a hora do servidor, o `t` do idioma e os formatos.
 */
import { GAME_SESSION_RATING, type GameSessionRatingBrief, type GameSessionRatings, type SessionRatingView, type TableRating } from '../../shared.js';
import type { Translate } from './clockModel.js';
import { LOCAL_SESSION_FORMATS, dayLabel, type SessionFormats } from './sessionsModel.js';

/** A média com uma casa, no jeito do idioma: "4,5" em português, "4.5" em inglês. */
export function formatAverage(average: number, language: string): string {
  return new Intl.NumberFormat(language, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(average);
}

/** A média de uma lista de notas (a dos mestres, no registro); null sem nota nenhuma. */
export function averageOf(ratings: readonly SessionRatingView[]): number | null {
  if (!ratings.length) return null;
  return Math.round((ratings.reduce((sum, rating) => sum + rating.stars, 0) / ratings.length) * 10) / 10;
}

/** O prazo, ao lado do título Avaliações. null: a sessão ainda está aberta (a avaliação abre no fim). */
export type RatingWindow = { open: boolean; text: string } | null;

/** "Abertas até sexta, 06/11" ou "Fechadas em 06/11". */
export function ratingWindow(t: Translate, closesAt: number | null, now: number, formats: SessionFormats = LOCAL_SESSION_FORMATS): RatingWindow {
  if (closesAt === null) return null;
  if (closesAt > now) return { open: true, text: t('sessions.rating.openUntil', { day: dayLabel(t, closesAt, true, formats) }) };
  return { open: false, text: t('sessions.rating.closedOn', { day: formats.day(closesAt) }) };
}

/**
 * O que o cartão de uma sessão encerrada mostra: a média das notas dos jogadores (para os mestres), a
 * nota que dei (se joguei) e, enquanto o prazo corre, "Avaliar": a minha nota ainda não foi dada, ou
 * algum jogador ainda não tem a minha (mestre).
 */
export type RatingBadge = { kind: 'average'; average: number; count: number } | { kind: 'mine'; stars: number } | { kind: 'rate' };

export function ratingBadges(brief: GameSessionRatingBrief | undefined, now: number): RatingBadge[] {
  if (!brief || brief.closesAt === null) return [];
  const open = brief.closesAt > now;
  const badges: RatingBadge[] = [];
  if (brief.average !== undefined && brief.average !== null && brief.count) badges.push({ kind: 'average', average: brief.average, count: brief.count });
  if (brief.mine) badges.push({ kind: 'mine', stars: brief.mine });
  if (open && (brief.mine === null || (brief.toRate ?? 0) > 0)) badges.push({ kind: 'rate' });
  return badges;
}

/** O que o leitor de tela ouve de cada selo, junto com o resto do cartão. */
export function badgeLabel(t: Translate, badge: RatingBadge, language: string): string {
  if (badge.kind === 'average') return t('sessions.rating.badge.average', { average: formatAverage(badge.average, language), count: badge.count });
  if (badge.kind === 'mine') return t('sessions.rating.badge.mine', { n: badge.stars });
  return t('sessions.rating.rate');
}

/** O que dá para fazer numa sessão encerrada, pelo registro: avaliar a sessão (joguei) e os jogadores (mestre). */
export function ratingRoles(ratings: GameSessionRatings | undefined): { player: boolean; gm: boolean } {
  return { player: ratings?.mine !== undefined, gm: ratings?.session !== undefined };
}

/**
 * O texto de uma linha do cartão "Nas mesas" do perfil, ao lado da média: quantas avaliações e, com
 * menos de 5 (sem a média), a partir de quantas ela aparece. null: nenhuma avaliação, e a linha some.
 */
export function tableRatingText(t: Translate, rating: TableRating | undefined, as: 'player' | 'gm'): string | null {
  if (!rating?.count) return null;
  const count = t(`profile.tables.${as}.${rating.count === 1 ? 'one' : 'many'}`, { n: rating.count });
  return rating.average === null ? t('profile.tables.few', { count, min: GAME_SESSION_RATING.profileMin }) : count;
}
